import { h } from "../../core/dom.js";
import { swipeToClose } from "./sheet.js";
import { plainText } from "../speech.js";
import { copy } from "../../config/copy.js";
import { probabilityBars } from "./probabilityBars.js";

export function renderMarkdownLite(text) {
  const escape = (value) => value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const inline = (value) => escape(value).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  const html = [];
  let list = [];
  const flush = () => {
    if (list.length) html.push(`<ul>${list.map((item) => `<li>${inline(item)}</li>`).join("")}</ul>`);
    list = [];
  };
  for (const raw of String(text).split("\n")) {
    const line = raw.trim();
    const bullet = line.match(/^(?:[-•*]|\d+[.)])\s+(.*)$/);
    if (bullet) {
      list.push(bullet[1]);
      continue;
    }
    flush();
    if (line) html.push(`<p>${inline(line.replace(/^#+\s*/, ""))}</p>`);
  }
  flush();
  return html.join("");
}

export class ResultSheet {
  constructor(app) {
    this.app = app;
    this.cleanups = [];
    this.element = h("div", { class: "sheet-backdrop hidden", onclick: (e) => { if (e.target === this.element) this.close(); } });
    document.body.append(this.element);
  }

  close(fromBack = false) {
    if (this.element.classList.contains("hidden")) return;
    this.app.speaker?.stop();
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups = [];
    this.element.classList.add("hidden");
    this.element.replaceChildren();
    if (!fromBack && this.release) this.release();
    this.release = null;
  }

  open({ result, imageDataUrl, source, historyId, engine = "quick", modelVersion }) {
    const { app } = this;
    const sheet = h("section", { class: "sheet", role: "dialog", "aria-modal": "true", "aria-label": "Risultato" });
    const explanation = h("div", { class: "explanation" });
    const feedbackBox = h("div", { class: "feedback" });
    sheet.append(
      h("button", { class: "sheet-close", "aria-label": "Chiudi", onclick: () => this.close() }, "✕"),
      imageDataUrl ? h("img", { class: "sheet-photo", src: imageDataUrl, alt: "Foto analizzata" }) : null,
      h("div", { class: "verdict" },
        h("div", { class: "verdict-emoji" }, result.top.emoji),
        h("div", {},
          h("h2", {}, result.uncertain ? `Forse ${result.top.label_it.toLowerCase()}` : result.top.label_it),
          h("p", { class: "muted" }, `Confidenza ${Math.round(result.confidence * 100)}%`),
          h("span", { class: `engine engine-${engine}` }, engine === "advanced" ? copy.engineAdvanced : copy.engineQuick))),
      h("p", { class: "verdict-desc" }, result.uncertain ? copy.uncertain : result.top.description_it),
      probabilityBars(result.ranking, result.ranking.length),
      this.#explainButton(result, imageDataUrl, source, explanation, historyId),
      explanation,
      this.#feedback(result, imageDataUrl, feedbackBox, historyId, engine, modelVersion),
      feedbackBox
    );
    this.element.replaceChildren(sheet);
    this.element.classList.remove("hidden");
    const swipe = swipeToClose(sheet, { onClose: () => this.close() });
    this.cleanups.push(() => swipe.detach());
    this.release = app.back?.push(() => this.close(true));
  }

  #explainButton(result, imageDataUrl, source, target, historyId) {
    const { app } = this;
    if (!app.explain.available) return null;
    const button = h("button", { class: "btn btn-secondary wide" }, "✨ Spiegazione dettagliata");
    button.addEventListener("click", async () => {
      if (!navigator.onLine) return app.toast(copy.offline, "warning");
      button.disabled = true;
      button.textContent = "Sto osservando il tuo cane…";
      try {
        const data = await app.explain.explain(result, imageDataUrl, { source });
        target.innerHTML = renderMarkdownLite(data.text);
        target.append(this.#listenButton(data.text));
        button.remove();
        if (historyId) app.history.update(historyId, { explanation: data.text }).catch(() => {});
      } catch (error) {
        app.reporter.report(error, "explain");
        app.toast(copy.explainError, "error");
        button.disabled = false;
        button.textContent = "✨ Riprova la spiegazione";
      }
    });
    return h("div", {}, button, h("p", { class: "fineprint" }, "La spiegazione usa l'intelligenza artificiale sulla foto: è un aiuto, non un parere veterinario."));
  }

  #listenButton(text) {
    const { app } = this;
    if (!app.speaker?.available) return h("span", {});
    const button = h("button", { class: "btn btn-ghost small", type: "button" }, "🔊 Ascolta");
    const unsubscribe = app.speaker.onChange?.((speaking) => {
      if (!button.disabled) button.textContent = speaking ? "⏹ Ferma" : "🔊 Ascolta";
    });
    if (unsubscribe) this.cleanups.push(unsubscribe);
    button.addEventListener("click", async () => {
      if (app.speaker.speaking) return app.speaker.stop();
      button.disabled = true;
      button.textContent = "Preparo la voce…";
      try {
        const spoken = await app.speaker.speak(plainText(text));
        if (!spoken) app.toast(copy.voiceError, "warning");
      } finally {
        button.disabled = false;
        button.textContent = app.speaker.speaking ? "⏹ Ferma" : "🔊 Ascolta";
      }
    });
    return button;
  }

  #feedback(result, imageDataUrl, box, historyId, engine, modelVersion) {
    const { app } = this;
    if (!app.feedback.available) return null;
    const send = async (correct, correctedLabel, shareImage) => {
      try {
        const saved = await app.feedback.send({ result, correct, correctedLabel, shareImage, imageDataUrl, engine, modelVersion });
        box.replaceChildren(h("p", { class: "muted" }, copy.feedbackThanks));
        row.remove();
        if (historyId) {
          app.history.update(historyId, { feedback: correct ? "ok" : correctedLabel, feedbackId: saved?.id || null }).catch(() => {});
        }
      } catch (error) {
        app.reporter.report(error, "feedback");
        app.toast(copy.feedbackError, "error");
      }
    };
    const consent = h("input", { type: "checkbox", id: "consent" });
    const consentLabel = h("label", { class: "consent", for: "consent" }, consent, " Condividi anche la foto per migliorare PAWS");
    const askCorrection = () => {
      const options = result.ranking.filter((c) => c.id !== result.top.id);
      box.replaceChildren(
        h("p", {}, "Come ti sembra invece?"),
        h("div", { class: "choice-grid" }, options.map((c) => h("button", { class: "btn btn-ghost", onclick: () => send(false, c.id, consent.checked) }, `${c.emoji} ${c.label_it}`))),
        consentLabel);
      row.remove();
    };
    const row = h("div", { class: "feedback-row" },
      h("span", {}, "Ho indovinato?"),
      h("button", { class: "btn btn-ghost", onclick: () => send(true, null, consent.checked) }, "👍 Sì"),
      h("button", { class: "btn btn-ghost", onclick: askCorrection }, "👎 No"));
    box.append(consentLabel);
    return row;
  }
}
